import { Box, Typography, Paper, Tooltip } from '@mui/material';
import { useState, useEffect } from 'react';
import { SECONDARY_TEXT } from './dashboardText';

interface DayData {
  date: string;
  value: number;
  minutes?: number;
}

interface ContributionHeatmapProps {
  weeklyIntensity: Array<Array<DayData>>;
  totalSessionsLastYear: number;
  longestStreakDays: number;
  currentStreakDays: number;
  totalActiveDays: number;
  onDayClick?: (date: string) => void;
}

export function ContributionHeatmap({
  weeklyIntensity,
  totalSessionsLastYear,
  longestStreakDays: _longestStreakDays,
  currentStreakDays: _currentStreakDays,
  totalActiveDays: _totalActiveDays,
  onDayClick,
}: ContributionHeatmapProps) {
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
  const allMonths: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const currentMonth: number = new Date().getMonth();
  const months: string[] = allMonths.concat(allMonths).slice(currentMonth + 1, currentMonth + 13);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const getContributionColor = (level: number): string => {
    const colors: string[] = [
      '#374151', // No contributions (gray-700)
      '#14532d', // Low (green-900)
      '#15803d', // Medium-low (green-700)
      '#22c55e', // Medium (green-500)
      '#4ade80', // High (green-400)
    ];
    return colors[Math.min(level, 4)];
  };

  // Find the actual last date from the data
  const lastWeek: DayData[] = weeklyIntensity[weeklyIntensity.length - 1];
  const lastDay: DayData = lastWeek[lastWeek.length - 1];
  const currentDate: Date = new Date(lastDay.date);

  const startDate: Date = new Date(currentDate);
  startDate.setDate(startDate.getDate() - 364); // Go back 364 days for a total of 365 days

  // Create a flat array of all days from the weekly data for easy lookup
  const dayDataMap: Map<string, DayData> = new Map();
  weeklyIntensity.forEach((week: DayData[]) => {
    week.forEach((day: DayData) => {
      dayDataMap.set(day.date, day);
    });
  });

  // Find the Sunday before or on the start date
  const firstSunday: Date = new Date(startDate);
  const startDayOfWeek: number = startDate.getDay();
  if (startDayOfWeek !== 0) {
    firstSunday.setDate(firstSunday.getDate() - startDayOfWeek);
  }

  // Find the Saturday after or on the current date
  const lastSaturday: Date = new Date(currentDate);
  const endDayOfWeek: number = currentDate.getDay();
  if (endDayOfWeek !== 6) {
    lastSaturday.setDate(lastSaturday.getDate() + (6 - endDayOfWeek));
  }

  // Build weeks properly: each week is exactly 7 days from Sunday to Saturday
  const paddedWeeks: DayData[][] = [];
  const weekStartDate: Date = new Date(firstSunday);

  while (weekStartDate <= lastSaturday) {
    const week: DayData[] = [];

    // Create exactly 7 days for this week (Sun-Sat)
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const dayDate: Date = new Date(weekStartDate);
      dayDate.setDate(dayDate.getDate() + dayIndex);
      const dateStr: string = dayDate.toISOString().split('T')[0];

      // Use existing data if available, otherwise create empty day
      const dayData: DayData = dayDataMap.get(dateStr) || {
        date: dateStr,
        value: 0,
        minutes: 0,
      };
      week.push(dayData);
    }

    paddedWeeks.push(week);

    // Move to next week (add 7 days)
    weekStartDate.setDate(weekStartDate.getDate() + 7);
  }

  return (
    <Paper
      sx={{
        backgroundColor: '#111827',
        color: '#ffffff',
        borderRadius: 2,
        padding: { xs: '24px 12px', sm: '24px 16px', md: 3 },
        boxShadow: 'none',
      }}
    >
      <Box sx={{ marginBottom: 2 }}>
        <Typography
          variant="h6"
          sx={{
            color: '#ffffff',
            fontSize: '1.125rem',
            marginBottom: 1,
            fontWeight: 500,
          }}
        >
          <Box component="span" sx={{ color: '#ffffff' }}>
            {totalSessionsLastYear.toLocaleString()}
          </Box>{' '}
          <Box component="span" sx={{ color: '#9ca3af' }}>
            play sessions in the last year
          </Box>
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.3, sm: 0.5 } }}>
        {/* Month labels */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: { xs: 0.5, sm: 1 },
            paddingX: { xs: 0.2, sm: 0.5 },
            overflow: 'hidden',
          }}
        >
          {months.map((month: string, index: number) => (
            <Typography
              key={index}
              sx={{
                color: SECONDARY_TEXT,
                fontSize: { xs: '0.625rem', sm: '0.75rem' },
                fontWeight: 400,
                whiteSpace: 'nowrap',
                textAlign: 'center',
                flex: 1,
              }}
            >
              {isSmallScreen ? month.slice(0, 1) : month}
            </Typography>
          ))}
        </Box>

        {/* Contribution grid */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 0.75 },
            justifyContent: 'space-between',
            overflow: 'hidden',
          }}
        >
          {paddedWeeks.map((week: DayData[], weekIndex: number) => (
            <Box
              key={weekIndex}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 0.75 },
                // MUS-607: where the weeks do not fit side by side (every
                // window up to about 1024px wide) the columns shrink below a
                // day's width and the last week's days ran 4px past the card's
                // edge. The last week keeps at least a day's width; where the
                // weeks fit, nothing changes.
                flex: weekIndex === paddedWeeks.length - 1 ? '1 0 auto' : '1 1 auto',
                minWidth: 0,
              }}
            >
              {week.map((day: DayData, dayIndex: number) => {
                const minutes: number = day.minutes || 0;
                const tooltipText: string = `${day.date}: ${day.value} sessions${
                  minutes > 0 ? `, ${minutes} minutes` : ''
                }`;

                // Check if this is a future date (past the last actual date)
                const dayDate: Date = new Date(day.date);
                const isFutureDate: boolean = dayDate > currentDate;
                const isClickable: boolean = !isFutureDate && Boolean(onDayClick);

                return (
                  <Tooltip
                    key={`${weekIndex}-${dayIndex}`}
                    title={tooltipText}
                    placement="top"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: '#374151',
                          color: '#ffffff',
                          fontSize: '0.75rem',
                          borderRadius: 1,
                          '& .MuiTooltip-arrow': {
                            color: '#374151',
                          },
                        },
                      },
                    }}
                  >
                    <Box
                      onClick={isClickable ? () => onDayClick?.(day.date) : undefined}
                      sx={{
                        width: { xs: '8px', sm: '10px', md: '12px', lg: '15px', xl: '18px' },
                        height: { xs: '8px', sm: '10px', md: '12px', lg: '15px', xl: '18px' },
                        borderRadius: { xs: '1px', sm: '2px', lg: '2.5px', xl: '3px' },
                        backgroundColor: isFutureDate ? '#1f2937' : getContributionColor(day.value),
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                        opacity: isFutureDate ? 0.3 : 1,
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}
